/**
 * Store Pinia des données statiques de production (composition API)
 */
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { ServiceOperationResult } from '../models/api/serviceOperationResult';
import { IDataOperationResult } from '../models/api/dataOperationResult';
import DataOperationError from '../models/api/dataOperationError';
import { IProductionStatic } from '../models/productionStatic';
import { IProductionStaticApiResponse } from '../models/api/productionStaticApiResponse';
import { IProductionStaticForm } from '../models/forms/productionStaticForm';
import { IProductionStaticFilter } from '../models/filters/productionStaticFilter';
import ProductionStaticServices from '../services/ProductionStaticService';
import { useProductionStore } from './production.store';
import i18n from "@/i18n";
import { useParameterStore } from './parameter.store';

/**
 * Store Pinia des données statiques de production utilisant la Composition API
 */
export const useProductionStaticStore = defineStore('ProductionStatics', () => {
    // State
    const datas = ref<IProductionStatic[]>([]);
    const current = ref<IProductionStatic>({} as IProductionStatic);
    const itemForm = ref<IProductionStaticForm>({} as IProductionStaticForm);

    // Actions

    /**
     * Hydrate une liste de données statiques en batch (optimisé pour la performance)
     * - Charge les stores dépendants une seule fois en parallèle
     * - Crée des Maps d'index pour des lookups O(1)
     *
     * @param statics Liste de données statiques reçues de l'API avec des IDs
     * @returns Liste de données statiques avec les références des stores
     */
    async function hydrateStatics(statics: IProductionStaticApiResponse[]): Promise<IProductionStatic[]> {
        if (statics.length === 0) return [];

        const parameterStore = useParameterStore();

        // Charger les stores dépendants en parallèle si nécessaire
        // NOTE: Ne PAS charger productionStore ici pour éviter la boucle circulaire
        // (productionStore.load → hydrateProductions → hydrateStatics → productionStore.load)
        const loadPromises: Promise<unknown>[] = [];
        if (parameterStore.datas.length === 0) {
            loadPromises.push(parameterStore.load({ page: 1, pageSize: 0 }));
        }
        if (loadPromises.length > 0) {
            await Promise.all(loadPromises);
        }

        // Maps O(1)
        const paramMap = new Map(parameterStore.datas.map(p => [p.id, p]));

        // Hydrater en une seule passe
        const models: IProductionStatic[] = [];
        for (const staticData of statics) {
            const model = { ...staticData } as unknown as IProductionStatic;
            // NOTE: model.production n'est pas hydraté ici pour éviter la référence circulaire
            model.animalClass = paramMap.get(staticData.animalClassId) ?? model.animalClass;
            model.animalStatus = staticData.animalStatusId ? paramMap.get(staticData.animalStatusId) : undefined;
            model.formule = staticData.formuleId ? paramMap.get(staticData.formuleId) : undefined;
            model.NN = staticData.nnId ? paramMap.get(staticData.nnId) : undefined;
            model.geneticType = staticData.geneticTypeId ? paramMap.get(staticData.geneticTypeId) : undefined;
            models.push(model);
        }
        return models;
    }

    /**
     * Chargement des données dans le store
     * @param filter Le filtre à appliquer pour le chargement des données
     * @returns Le résultat de l'opération
     */
    async function load(filter: IProductionStaticFilter) {
        const result = await ProductionStaticServices.getAll<IProductionStaticFilter, IProductionStaticApiResponse>(filter);
        datas.value = [];
        if (!result.error) {
            datas.value = await hydrateStatics(result.data as IProductionStaticApiResponse[]);
        }
        return result;
    }

    /**
     * Récupération d'un élément par son identifiant
     * @param id L'identifiant de l'élément à récupérer
     * @returns Le résultat de l'opération
     */
    async function get(id: number) {
        const result = await ProductionStaticServices.getById<IProductionStaticApiResponse>(id);
        if (!result.error) {
            const [modelData] = await hydrateStatics([result.data as IProductionStaticApiResponse]);
            const index = datas.value.findIndex((item) => item.id === id);
            if (index !== -1) {
                datas.value[index] = modelData;
            }
            current.value = modelData;
            itemForm.value = toForm(modelData);
        }
        return result;
    }

    /**
     * Sauvegarde d'un élément
     * @returns Le résultat de l'opération
     */
    async function save(maj: boolean = true, isPatch: boolean = false) {
        let result: ServiceOperationResult<IProductionStaticApiResponse | IDataOperationResult<IProductionStaticApiResponse>>;
        if (itemForm.value.id > 0) {
            result = await ProductionStaticServices.update(itemForm.value);
            if (!result.error) {
                const [modelData] = await hydrateStatics([result.data as IProductionStaticApiResponse]);
                current.value = modelData;
                const index = datas.value.findIndex((item) => item.id === itemForm.value.id);
                if (index !== -1) {
                    datas.value[index] = current.value;
                } else {
                    const error = new DataOperationError();
                    error.REQUEST_ERROR = i18n.global.t("errors.itemNotFoundInStore");
                    return new ServiceOperationResult<IDataOperationResult<IProductionStatic>>(true, { errors: error });
                }
            }
        } else {
            result = await ProductionStaticServices.create(itemForm.value);
            if (!result.error) {
                const [modelData] = await hydrateStatics([result.data as IProductionStaticApiResponse]);
                current.value = modelData;
                datas.value.push(current.value);
            }
        }
        return result;
    }

    /**
     * Mappage de l'élément current en formulaire
     * @param item L'élément à mapper
     * @returns Le formulaire mappé
     */
    function toForm(item: IProductionStatic): IProductionStaticForm {
        const form = {} as IProductionStaticForm;

        form.id = item.id;
        form.productionId = item.production?.id ?? 0;
        form.animalClassId = item.animalClass?.id ?? 0;
        form.animalStatusId = item.animalStatus?.id;
        form.animalWeight = item.animalWeight;
        form.formuleId = item.formule?.id;
        form.parameterA = item.parameterA;
        form.parameterB = item.parameterB;
        form.parameterC = item.parameterC;
        form.parameterD = item.parameterD;
        form.thresholdConcentration = item.thresholdConcentration;
        form.deviderConcentration = item.deviderConcentration;
        form.prcPredilution = item.prcPredilution;
        form.thresholdPrcLiving = item.thresholdPrcLiving;
        form.thresholdMotility = item.thresholdMotility;
        form.minPumpVolume = item.MinPumpVolume;
        form.index = item.index;
        form.geneticTypeId = item.geneticType?.id;
        form.armada = item.armada;
        form.nador = item.nador;
        form.nnId = item.NN?.id;

        return form;
    }

    /**
     * Création d'un nouvel élément en formulaire
     */
    function newForm(lienId: number): IProductionStaticForm {
        itemForm.value = {
            id: 0,
            productionId: lienId,
            animalClassId: 0,
            animalWeight: 0,
            parameterA: 0,
            parameterB: 0,
            parameterC: 0,
            parameterD: 0,
            thresholdConcentration: 0,
            deviderConcentration: 0,
            prcPredilution: 0,
            thresholdPrcLiving: 0,
            thresholdMotility: 0,
            armada: false,
            nador: false,
            minPumpVolume: 0,
            index: 0
        } as IProductionStaticForm;
        return itemForm.value;
    }

    /**
     * Création d'un nouvel item
     */
    function newItem(): IProductionStatic {
        current.value = {
            id: 0,
            productionId: 0,
            production: {} as any,
            animalClassId: 0,
            animalClass: {} as any,
            animalWeight: 0,
            parameterA: 0,
            parameterB: 0,
            parameterC: 0,
            parameterD: 0,
            thresholdConcentration: 0,
            deviderConcentration: 0,
            prcPredilution: 0,
            thresholdPrcLiving: 0,
            thresholdMotility: 0,
            armada: false,
            nador: false,
            MinPumpVolume: 0,
            index: 0
        } as IProductionStatic;
        itemForm.value = toForm(current.value);
        return current.value;
    }

    return {
        // State
        datas,
        current,
        itemForm,

        // Actions
        hydrateStatics,
        load,
        get,
        save,
        toForm,
        newForm,
        newItem
    };
});
